/**
 * Plan의 frequency 기반으로 발생 날짜·Action 생성 유틸 (로컬 캘린더 기준 yyyy-MM-dd)
 */

import { DreamityPlanResDto, type PlanFrequency } from '@/store/types/plan.types'
import { DreamityActionSaveReqDto } from '@/store/types/calendar.types'

const KNOWN: PlanFrequency[] = ['NONE', 'DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY']

/**
 * 스냅샷·마이그레이션 데이터에서 빈도 문자열이 비어 있거나 대소문자가 섞여 있어도 캘린더·액션 생성이 동작하도록 정규화
 */
export function normalizePlanFrequency(
  raw: DreamityPlanResDto['frequency'] | string | null | undefined,
  plan?: Pick<DreamityPlanResDto, 'startDate' | 'endDate'>
): PlanFrequency {
  if (raw != null && String(raw).trim() !== '') {
    const u = String(raw).trim().toUpperCase()
    if ((KNOWN as string[]).includes(u)) return u as PlanFrequency
  }
  if (plan?.startDate && plan.startDate === plan.endDate) return 'NONE'
  return 'DAILY'
}

function parseYmdLocal(ymd: string): Date {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd.trim())
  if (!m) return new Date(NaN)
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
}

function formatYmdLocal(d: Date): string {
  const y = d.getFullYear()
  const mo = String(d.getMonth() + 1).padStart(2, '0')
  const da = String(d.getDate()).padStart(2, '0')
  return `${y}-${mo}-${da}`
}

/**
 * 해당 로컬 날짜에 plan의 반복 규칙이 한 번 발생하는지
 */
export function planOccursOnLocalDate(plan: DreamityPlanResDto, d: Date, planStart: Date): boolean {
  // 시간 제거하고 날짜만 비교하기 위해 복사본 생성
  const checkDate = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  const startDate = new Date(planStart.getFullYear(), planStart.getMonth(), planStart.getDate()).getTime()
  const frequency = normalizePlanFrequency(plan.frequency, plan)

  switch (frequency) {
    case 'NONE':
      return checkDate === startDate

    case 'DAILY':
      return true

    case 'WEEKLY': {
      if (plan.frequencyDay !== null && plan.frequencyDay !== undefined) {
        const jsDay = plan.frequencyDay === 7 ? 0 : plan.frequencyDay
        return d.getDay() === jsDay
      }
      return d.getDay() === planStart.getDay()
    }

    case 'MONTHLY': {
      const targetDay = plan.frequencyDay ?? planStart.getDate()
      const lastDayOfMonth = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()
      return d.getDate() === Math.min(targetDay, lastDayOfMonth)
    }

    case 'YEARLY': {
      if (d.getMonth() !== planStart.getMonth()) return false
      const targetDay = planStart.getDate()
      const lastDayOfMonth = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate()
      return d.getDate() === Math.min(targetDay, lastDayOfMonth)
    }

    default:
      return false
  }
}

/**
 * plan 기간 ∩ [rangeStartYmd, rangeEndYmd] 안에서 발생하는 날짜(yyyy-MM-dd) 목록
 */
export function listOccurrenceYmdsInRange(
  plan: DreamityPlanResDto,
  rangeStartYmd: string,
  rangeEndYmd: string
): string[] {
  if (plan.isCompleted) return []

  const frequency = normalizePlanFrequency(plan.frequency, plan)
  const planStart = parseYmdLocal(plan.startDate)
  const endYmdEffective = frequency === 'NONE' ? plan.startDate : plan.endDate
  const planEnd = parseYmdLocal(endYmdEffective)
  const rStart = parseYmdLocal(rangeStartYmd)
  const rEnd = parseYmdLocal(rangeEndYmd)
  if ([planStart, planEnd, rStart, rEnd].some((x) => Number.isNaN(x.getTime()))) return []

  if (frequency !== 'NONE' && planEnd.getTime() < planStart.getTime()) return []

  // 시작점은 계획 시작일과 조회 시작일 중 늦은 날
  const cur = new Date(Math.max(planStart.getTime(), rStart.getTime()))
  cur.setHours(0, 0, 0, 0)
  
  // 종료점은 계획 종료일과 조회 종료일 중 빠른 날
  const end = new Date(Math.min(planEnd.getTime(), rEnd.getTime()))
  end.setHours(0, 0, 0, 0)

  const out: string[] = []
  while (cur <= end) {
    if (planOccursOnLocalDate(plan, cur, planStart)) {
      out.push(formatYmdLocal(cur))
    }
    cur.setDate(cur.getDate() + 1)
  }
  return out
}

/**
 * Plan의 frequency와 기간에 따라 Action 목록 생성 (저장용 DTO)
 */
export function generateActionsFromPlan(plan: DreamityPlanResDto): DreamityActionSaveReqDto[] {
  const ymds = listOccurrenceYmdsInRange(plan, plan.startDate, plan.endDate)
  return ymds.map((actionDate) => ({
    planId: plan.planId,
    actionDate,
    actionTime: '09:00:00',
    actionName: plan.planName,
    actionDesc: plan.planDesc || plan.planName,
    isCompleted: false,
  }))
}

/**
 * Plan의 frequency 기반으로 Action 생성 (제한된 수만큼)
 */
export function generateLimitedActionsFromPlan(
  plan: DreamityPlanResDto,
  maxActions: number = 100
): DreamityActionSaveReqDto[] {
  const allActions = generateActionsFromPlan(plan)
  return allActions.slice(0, maxActions)
}
